import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, CheckCircle2, Loader2, ShieldCheck } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/context/AuthContext';

/* Founding-access signup. Tier names are landing-page vocabulary (Tradesman /
 * Foreman / The Boss); the stored tier value is tradesman | foreman | boss.
 * Pre-launch: all signups are founding members, NO card taken. */
const TIERS = [
  { value: 'tradesman', label: 'Tradesman', price: '$99',  blurb: 'Sole operator' },
  { value: 'foreman',   label: 'Foreman',   price: '$169', blurb: 'Small crew (2–5)' },
  { value: 'boss',      label: 'The Boss',  price: '$269', blurb: 'Multi-crew boss' },
];

export default function SignupPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { signUp } = useAuth();

  const initialTier = useMemo(() => {
    const t = (params.get('tier') || '').toLowerCase();
    return TIERS.some((x) => x.value === t) ? t : 'foreman';
  }, [params]);

  // Attribution for the engagement ledger (K.O. Command, migration 071):
  // the marketing site passes utm tags on its signup links; keep them with
  // the referrer so the signup is recorded with the post or page that
  // brought the person here. Never shown; never required.
  const attribution = useMemo(() => {
    const pick = (k) => (params.get(k) || '').slice(0, 120);
    return {
      utm_source: pick('utm_source'),
      utm_medium: pick('utm_medium'),
      utm_campaign: pick('utm_campaign'),
      utm_content: pick('utm_content'),
      referrer: (typeof document !== 'undefined' ? document.referrer : '').slice(0, 500),
      landing_page: (typeof window !== 'undefined' ? window.location.href.split('#')[0] : '').slice(0, 500),
    };
  }, [params]);

  const [fullName, setFullName] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [tier, setTier] = useState(initialTier);
  const [agreeTos, setAgreeTos] = useState(false);
  const [marketing, setMarketing] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!fullName.trim()) return setError('Enter your full name.');
    if (!businessName.trim()) return setError('Enter your business name.');
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) return setError('Enter a valid email.');
    if (password.length < 8) return setError('Password must be at least 8 characters.');
    if (!agreeTos) return setError('Please accept the Terms of Service to continue.');

    setSubmitting(true);
    try {
      const { data, error: signUpError } = await signUp({
        email: email.trim(),
        password,
        fullName: fullName.trim(),
        businessName: businessName.trim(),
        tier,
        phone: phone.trim(),
        marketingOptIn: marketing,
        attribution,
      });
      if (signUpError) {
        const msg = (signUpError.message || '').toLowerCase();
        if (msg.includes('already') || msg.includes('registered')) {
          setError('That email already has an account. Try signing in instead.');
        } else {
          setError(signUpError.message || 'Could not create your account. Please try again.');
        }
        return;
      }
      // Email confirmation OFF → a session is returned; go straight to onboarding.
      if (data?.session) {
        navigate('/onboarding', { replace: true });
        return;
      }
      // Email confirmation ON → tell them to verify.
      setSent(true);
    } catch (err) {
      setError(err?.message || 'Could not create your account. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-background grain-overlay">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[hsl(var(--primary))]/40 to-transparent" />
      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          <div className="mb-6 flex flex-col items-center text-center space-y-3">
            <img src="/kocore-logo.png" alt="K.O. CO+RE Systems" className="h-28 w-auto" />
            <div className="text-[10px] uppercase tracking-[0.32em] text-[hsl(var(--primary))]">
              Founding Access
            </div>
            <p className="text-sm text-muted-foreground max-w-xs">
              A fair system in an unfair world. Set up your shop in a couple minutes.
            </p>
          </div>

          <Card className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))] shadow-2xl shadow-black/40">
            <CardContent className="p-6 sm:p-8">
              {sent ? (
                <div className="text-center py-6" data-testid="signup-sent">
                  <CheckCircle2 className="mx-auto h-10 w-10 text-[hsl(var(--success))]" />
                  <h2 className="mt-3 text-lg font-semibold text-foreground">You're in.</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Check your email to verify — takes about as long as a safety meeting.
                    Once you confirm, you'll land right in setup.
                  </p>
                  <Button
                    onClick={() => navigate('/login')}
                    variant="outline"
                    className="mt-5 border-border text-foreground hover:bg-[hsl(var(--muted))] uppercase tracking-[0.16em] text-xs"
                  >
                    Go to sign in
                  </Button>
                </div>
              ) : (
                <form onSubmit={submit} className="space-y-4" data-testid="signup-form" noValidate>
                  <Field label="Full name" htmlFor="su-name">
                    <Input id="su-name" value={fullName} onChange={(e) => setFullName(e.target.value)}
                      placeholder="Jane Contractor" className="bg-[hsl(var(--muted))] border-border text-foreground"
                      data-testid="signup-name" />
                  </Field>
                  <Field label="Business name" htmlFor="su-biz">
                    <Input id="su-biz" value={businessName} onChange={(e) => setBusinessName(e.target.value)}
                      placeholder="Jane's Concrete & Remodel" className="bg-[hsl(var(--muted))] border-border text-foreground"
                      data-testid="signup-business" />
                  </Field>
                  <Field label="Email" htmlFor="su-email">
                    <Input id="su-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com" autoComplete="email"
                      className="bg-[hsl(var(--muted))] border-border text-foreground" data-testid="signup-email" />
                  </Field>
                  <Field label="Password" htmlFor="su-pass" hint="At least 8 characters">
                    <Input id="su-pass" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••" autoComplete="new-password"
                      className="bg-[hsl(var(--muted))] border-border text-foreground" data-testid="signup-password" />
                  </Field>
                  <Field label="Phone" htmlFor="su-phone" hint="Optional, but we'll text you when it matters">
                    <Input id="su-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                      placeholder="(555) 123-4567" className="bg-[hsl(var(--muted))] border-border text-foreground"
                      data-testid="signup-phone" />
                  </Field>

                  {/* Tier selector */}
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-1.5">
                      Choose your plan
                    </div>
                    <div className="grid grid-cols-1 gap-2" data-testid="signup-tiers">
                      {TIERS.map((tp) => (
                        <button key={tp.value} type="button" onClick={() => setTier(tp.value)}
                          data-testid={`signup-tier-${tp.value}`}
                          className={cn(
                            'flex items-center justify-between rounded-md border px-3 py-2.5 text-left transition-colors',
                            tier === tp.value
                              ? 'border-[hsl(var(--primary))]/50 bg-[hsl(var(--primary))]/10'
                              : 'border-border hover:bg-[hsl(var(--muted))]'
                          )}>
                          <span>
                            <span className={cn('text-sm font-semibold',
                              tier === tp.value ? 'text-[hsl(var(--primary))]' : 'text-foreground')}>
                              {tp.label}
                            </span>
                            <span className="block text-[11px] text-muted-foreground">{tp.blurb}</span>
                          </span>
                          <span className="text-sm font-semibold text-foreground tabular-nums">
                            {tp.price}<span className="text-[11px] text-muted-foreground">/mo</span>
                          </span>
                        </button>
                      ))}
                    </div>
                    <p className="mt-1.5 text-[11px] text-muted-foreground">
                      No card required during founding access. Lock your rate now.
                    </p>
                  </div>

                  {/* Legal */}
                  <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer">
                    <input type="checkbox" checked={agreeTos} onChange={(e) => setAgreeTos(e.target.checked)}
                      className="mt-0.5 h-4 w-4 accent-[hsl(var(--primary))]" data-testid="signup-tos" />
                    <span>I agree to the <a href="https://kocoresystems.com/terms" target="_blank" rel="noreferrer"
                      className="text-[hsl(var(--primary))] underline">Terms of Service</a>.</span>
                  </label>
                  <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer">
                    <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)}
                      className="mt-0.5 h-4 w-4 accent-[hsl(var(--primary))]" data-testid="signup-marketing" />
                    <span>Send me product updates and founding-member perks.</span>
                  </label>

                  {error ? (
                    <Alert className="border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10 text-foreground"
                      data-testid="signup-error">
                      <AlertCircle className="h-4 w-4 text-[hsl(var(--destructive))]" />
                      <AlertDescription className="text-sm">{error}</AlertDescription>
                    </Alert>
                  ) : null}

                  <Button type="submit" disabled={submitting}
                    className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 uppercase tracking-[0.18em] text-xs"
                    data-testid="signup-submit">
                    {submitting ? <Loader2 size={14} className="mr-2 animate-spin" /> : null}
                    Get founding access
                  </Button>

                  <div className="flex items-center justify-center gap-2 text-[11px] text-muted-foreground">
                    <ShieldCheck size={12} className="text-[hsl(var(--success))]" />
                    Secure signup · no card taken
                  </div>
                  <div className="text-center text-xs text-muted-foreground">
                    Already have an account?{' '}
                    <Link to="/login" className="text-[hsl(var(--primary))] underline">Sign in</Link>
                  </div>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, htmlFor, hint, children }) => (
  <div>
    <label htmlFor={htmlFor} className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
      {label}
    </label>
    <div className="mt-1">{children}</div>
    {hint ? <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p> : null}
  </div>
);
